import React, { useState, useCallback } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../components/ui/tooltip";
import { 
  Calculator, 
  TrendingUp, 
  TrendingDown, 
  DollarSign, 
  Package, 
  BarChart3, 
  Target,
  AlertCircle,
  Info,
  ChevronUp,
  ChevronDown,
  Minus,
  Loader2
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatCurrency = (value) => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(value);
};

const formatNumber = (value) => {
  return new Intl.NumberFormat('pt-BR').format(value);
};

const formatPercentage = (value) => {
  return `${value >= 0 ? '+' : ''}${value.toFixed(2)}%`;
};

const ChangeIndicator = ({ value, showIcon = true }) => {
  if (value === 0) {
    return (
      <span className="result-change neutral">
        {showIcon && <Minus className="w-3 h-3" />}
        0%
      </span>
    );
  }
  
  const isPositive = value > 0;
  return (
    <span className={`result-change ${isPositive ? 'positive' : 'negative'}`}>
      {showIcon && (isPositive ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />)}
      {formatPercentage(value)}
    </span>
  );
};

const ResultMetric = ({ label, value, change, icon: Icon }) => (
  <div className="bg-white rounded-lg p-3 border border-slate-100 shadow-sm" data-testid={`result-${label.toLowerCase().replace(/\s/g, '-')}`}>
    <div className="flex items-center gap-1.5 mb-1">
      {Icon && <Icon className="w-3.5 h-3.5 text-infoprice-blue flex-shrink-0" />}
      <span className="text-xs text-slate-500 font-medium">{label}</span>
    </div>
    <div className="font-bold text-base text-infoprice-navy">{value}</div>
    {change !== undefined && (
      <div className="mt-1">
        <ChangeIndicator value={change} />
      </div>
    )}
  </div>
);

const EmptyResults = () => (
  <div className="text-center py-8">
    <BarChart3 className="w-12 h-12 mx-auto text-infoprice-gray500 opacity-50 mb-3" />
    <p className="text-sm text-slate-500">
      Preencha os dados e clique em calcular
    </p>
  </div>
);

const PricingCalculator = () => {
  const [scenario, setScenario] = useState({
    productName: "",
    currentPrice: "",
    unitCost: "",
    monthlyVolume: "",
    elasticity: ""
  });

  const [strategyType, setStrategyType] = useState("");
  const [strategyParams, setStrategyParams] = useState({
    percentage: "",
    direction: "increase",
    targetMargin: "",
    fixedPrice: "",
    targetType: "profit",
    targetPercentage: "",
    targetDirection: "increase"
  });

  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleScenarioChange = (field, value) => {
    setScenario(prev => ({ ...prev, [field]: value }));
    setError(null);
  };

  const handleStrategyParamChange = (field, value) => {
    setStrategyParams(prev => ({ ...prev, [field]: value }));
    setError(null);
  };

  const validateInputs = () => {
    const { currentPrice, unitCost, monthlyVolume, elasticity } = scenario;
    
    if (!currentPrice || !unitCost || !monthlyVolume || !elasticity) {
      return "Preencha todos os campos do cenário atual.";
    }

    const price = parseFloat(currentPrice);
    const cost = parseFloat(unitCost);
    const volume = parseInt(monthlyVolume);

    if (isNaN(price) || price <= 0) return "Preço deve ser maior que zero.";
    if (isNaN(cost) || cost < 0) return "Custo deve ser maior ou igual a zero.";
    if (cost >= price) return "Custo deve ser menor que o preço.";
    if (isNaN(volume) || volume <= 0) return "Volume deve ser maior que zero.";
    if (!strategyType) return "Selecione uma estratégia.";

    if (strategyType === "percentage") {
      const pct = parseFloat(strategyParams.percentage);
      if (isNaN(pct) || pct <= 0 || pct > 100) return "Percentual deve estar entre 0 e 100.";
    }
    if (strategyType === "margin") {
      const margin = parseFloat(strategyParams.targetMargin);
      if (isNaN(margin) || margin <= 0 || margin >= 100) return "Margem deve estar entre 0 e 100.";
    }
    if (strategyType === "fixed_price") {
      const fixedPrice = parseFloat(strategyParams.fixedPrice);
      if (isNaN(fixedPrice) || fixedPrice <= 0) return "Preço fixo deve ser maior que zero.";
      if (fixedPrice <= cost) return "Preço fixo deve ser maior que o custo.";
    }
    if (strategyType === "target") {
      const targetPct = parseFloat(strategyParams.targetPercentage);
      if (isNaN(targetPct) || targetPct <= 0) return "Percentual da meta deve ser maior que zero.";
    }
    return null;
  };

  const buildStrategy = () => {
    switch (strategyType) {
      case "percentage":
        return { type: "percentage", percentage: parseFloat(strategyParams.percentage), direction: strategyParams.direction };
      case "margin":
        return { type: "margin", target_margin: parseFloat(strategyParams.targetMargin) };
      case "fixed_price":
        return { type: "fixed_price", fixed_price: parseFloat(strategyParams.fixedPrice) };
      case "target":
        return { type: "target", target_type: strategyParams.targetType, percentage: parseFloat(strategyParams.targetPercentage), direction: strategyParams.targetDirection };
      default:
        return null;
    }
  };

  const handleCalculate = useCallback(async () => {
    const validationError = validateInputs();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const requestData = {
        scenario: {
          product_name: scenario.productName || "Produto",
          current_price: parseFloat(scenario.currentPrice),
          unit_cost: parseFloat(scenario.unitCost),
          monthly_volume: parseInt(scenario.monthlyVolume),
          elasticity: parseFloat(scenario.elasticity)
        },
        strategy: buildStrategy()
      };

      const response = await axios.post(`${API}/calculate`, requestData, { timeout: 10000 });
      
      if (response.data.success) {
        setResults(response.data);
      } else {
        setError(response.data.error || "Erro ao calcular.");
      }
    } catch (err) {
      console.error("Calculation error:", err);
      if (err.code === "ECONNABORTED") {
        setError("Tempo limite excedido. Tente novamente.");
      } else if (!err.response) {
        setError("Servidor indisponível. Verifique sua conexão.");
      } else {
        setError("Erro ao processar. Tente novamente.");
      }
    } finally {
      setLoading(false);
    }
  }, [scenario, strategyParams, strategyType]);

  const elasticityOptions = [
    { value: "-0.5", label: "Baixa Elasticidade", desc: "Clientes não são muito sensíveis. Compram mesmo se o preço subir um pouco." },
    { value: "-1.5", label: "Média Elasticidade", desc: "Clientes são moderadamente sensíveis. Mudanças de preço afetam vendas de forma equilibrada." },
    { value: "-3", label: "Alta Elasticidade", desc: "Clientes são muito sensíveis. Qualquer mudança de preço afeta muito as vendas." }
  ];

  return (
    <TooltipProvider>
      <div className="bg-slate-50 min-h-screen p-3 lg:p-4">
        {/* Compact Disclaimer */}
        <div className="max-w-7xl mx-auto mb-3">
          <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 flex items-center gap-2 text-xs text-amber-800">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            <span><strong>Importante:</strong> Estes cálculos são baseados em dados médios do mercado. Resultados mais precisos requerem dados reais e específicos.</span>
          </div>
        </div>

        {/* Main 3-Column Layout */}
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-3 lg:gap-4">
          
          {/* Left Column - Current Scenario */}
          <div className="lg:col-span-4">
            <Card className="h-full">
              <CardHeader className="pb-2 pt-3 px-4">
                <CardTitle className="text-base font-heading font-bold text-infoprice-navy flex items-center gap-2">
                  <Package className="w-4 h-4 text-infoprice-blue" />
                  Cenário Atual
                </CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-4 space-y-3">
                {/* Product Name */}
                <div>
                  <Label className="text-xs font-semibold text-slate-600 mb-1 block">Produto</Label>
                  <Input
                    data-testid="input-product-name"
                    placeholder="Ex: Arroz"
                    value={scenario.productName}
                    onChange={(e) => handleScenarioChange('productName', e.target.value)}
                    className="h-9 text-sm"
                  />
                </div>

                {/* Price and Cost in row */}
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs font-semibold text-slate-600 mb-1 block">Preço Atual (R$)</Label>
                    <Input
                      data-testid="input-current-price"
                      type="number"
                      step="0.01"
                      placeholder="50,00"
                      value={scenario.currentPrice}
                      onChange={(e) => handleScenarioChange('currentPrice', e.target.value)}
                      className="h-9 text-sm"
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-semibold text-slate-600 mb-1 block">Custo Unit. (R$)</Label>
                    <Input
                      data-testid="input-unit-cost"
                      type="number"
                      step="0.01"
                      placeholder="20,00"
                      value={scenario.unitCost}
                      onChange={(e) => handleScenarioChange('unitCost', e.target.value)}
                      className="h-9 text-sm"
                    />
                  </div>
                </div>

                {/* Volume */}
                <div>
                  <Label className="text-xs font-semibold text-slate-600 mb-1 block">Volume Mensal (un.)</Label>
                  <Input
                    data-testid="input-monthly-volume"
                    type="number"
                    placeholder="100"
                    value={scenario.monthlyVolume}
                    onChange={(e) => handleScenarioChange('monthlyVolume', e.target.value)}
                    className="h-9 text-sm"
                  />
                </div>

                {/* Elasticity */}
                <div>
                  <Label className="text-xs font-semibold text-slate-600 mb-1.5 block flex items-center gap-1">
                    Reação dos Clientes a Mudanças de Preço
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Info className="w-3 h-3 text-slate-400 cursor-help" />
                      </TooltipTrigger>
                      <TooltipContent className="max-w-xs">
                        <p>Como você acha que seus clientes reagem se você mudar o preço?</p>
                      </TooltipContent>
                    </Tooltip>
                  </Label>
                  <RadioGroup
                    value={scenario.elasticity}
                    onValueChange={(value) => handleScenarioChange('elasticity', value)}
                    className="space-y-2"
                    data-testid="radio-elasticity"
                  >
                    {elasticityOptions.map((opt) => (
                      <div
                        key={opt.value}
                        className={`border rounded-lg p-2.5 cursor-pointer transition-all ${
                          scenario.elasticity === opt.value 
                            ? 'border-infoprice-blue bg-infoprice-lightblue/30' 
                            : 'border-slate-200 hover:border-slate-300'
                        }`}
                        onClick={() => handleScenarioChange('elasticity', opt.value)}
                      >
                        <div className="flex items-start gap-2">
                          <RadioGroupItem 
                            value={opt.value} 
                            id={`el-${opt.value}`}
                            data-testid={`radio-elasticity-${opt.value}`}
                            className="mt-0.5"
                          />
                          <Label htmlFor={`el-${opt.value}`} className="cursor-pointer flex-1">
                            <span className="font-semibold text-sm block text-infoprice-navy">{opt.label}</span>
                            <span className="text-xs text-slate-500">{opt.desc}</span>
                          </Label>
                        </div>
                      </div>
                    ))}
                  </RadioGroup>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Center Column - Strategy */}
          <div className="lg:col-span-4">
            <Card className="h-full">
              <CardHeader className="pb-2 pt-3 px-4">
                <CardTitle className="text-base font-heading font-bold text-infoprice-navy flex items-center gap-2">
                  <Target className="w-4 h-4 text-infoprice-blue" />
                  Defina Sua Estratégia
                </CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-4">
                <RadioGroup
                  value={strategyType}
                  onValueChange={setStrategyType}
                  className="space-y-2"
                  data-testid="radio-strategy"
                >
                  {/* Strategy 1 */}
                  <div 
                    className={`border rounded-lg p-2.5 cursor-pointer transition-all ${strategyType === 'percentage' ? 'border-infoprice-blue bg-infoprice-lightblue/30' : 'border-slate-200'}`}
                    onClick={() => setStrategyType('percentage')}
                  >
                    <div className="flex items-center gap-2">
                      <RadioGroupItem value="percentage" data-testid="radio-strategy-percentage" />
                      <Label className="cursor-pointer font-semibold text-sm">Ajustar Preço %</Label>
                    </div>
                    {strategyType === 'percentage' && (
                      <div className="flex gap-2 mt-2 ml-6">
                        <Input
                          type="number"
                          placeholder="%"
                          value={strategyParams.percentage}
                          onChange={(e) => handleStrategyParamChange('percentage', e.target.value)}
                          className="w-16 h-8 text-sm"
                          data-testid="input-percentage"
                        />
                        <Select value={strategyParams.direction} onValueChange={(v) => handleStrategyParamChange('direction', v)}>
                          <SelectTrigger className="w-28 h-8 text-sm" data-testid="select-direction">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="increase">mais caro</SelectItem>
                            <SelectItem value="decrease">mais barato</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </div>

                  {/* Strategy 2 */}
                  <div 
                    className={`border rounded-lg p-2.5 cursor-pointer transition-all ${strategyType === 'margin' ? 'border-infoprice-blue bg-infoprice-lightblue/30' : 'border-slate-200'}`}
                    onClick={() => setStrategyType('margin')}
                  >
                    <div className="flex items-center gap-2">
                      <RadioGroupItem value="margin" data-testid="radio-strategy-margin" />
                      <Label className="cursor-pointer font-semibold text-sm">Margem de Lucro Alvo</Label>
                    </div>
                    {strategyType === 'margin' && (
                      <div className="flex items-center gap-2 mt-2 ml-6">
                        <Input
                          type="number"
                          placeholder="30"
                          value={strategyParams.targetMargin}
                          onChange={(e) => handleStrategyParamChange('targetMargin', e.target.value)}
                          className="w-16 h-8 text-sm"
                          data-testid="input-target-margin"
                        />
                        <span className="text-sm text-slate-600">% margem</span>
                      </div>
                    )}
                  </div>

                  {/* Strategy 3 */}
                  <div 
                    className={`border rounded-lg p-2.5 cursor-pointer transition-all ${strategyType === 'fixed_price' ? 'border-infoprice-blue bg-infoprice-lightblue/30' : 'border-slate-200'}`}
                    onClick={() => setStrategyType('fixed_price')}
                  >
                    <div className="flex items-center gap-2">
                      <RadioGroupItem value="fixed_price" data-testid="radio-strategy-fixed" />
                      <Label className="cursor-pointer font-semibold text-sm">Preço Fixo</Label>
                    </div>
                    {strategyType === 'fixed_price' && (
                      <div className="flex items-center gap-2 mt-2 ml-6">
                        <span className="text-sm text-slate-600">R$</span>
                        <Input
                          type="number"
                          placeholder="49,90"
                          value={strategyParams.fixedPrice}
                          onChange={(e) => handleStrategyParamChange('fixedPrice', e.target.value)}
                          className="w-24 h-8 text-sm"
                          data-testid="input-fixed-price"
                        />
                      </div>
                    )}
                  </div>

                  {/* Strategy 4 */}
                  <div 
                    className={`border rounded-lg p-2.5 cursor-pointer transition-all ${strategyType === 'target' ? 'border-infoprice-blue bg-infoprice-lightblue/30' : 'border-slate-200'}`}
                    onClick={() => setStrategyType('target')}
                  >
                    <div className="flex items-center gap-2">
                      <RadioGroupItem value="target" data-testid="radio-strategy-target" />
                      <Label className="cursor-pointer font-semibold text-sm">Meta Específica</Label>
                    </div>
                    {strategyType === 'target' && (
                      <div className="space-y-2 mt-2 ml-6">
                        <div className="flex gap-2">
                          <Input
                            type="number"
                            placeholder="%"
                            value={strategyParams.targetPercentage}
                            onChange={(e) => handleStrategyParamChange('targetPercentage', e.target.value)}
                            className="w-14 h-8 text-sm"
                            data-testid="input-target-percentage"
                          />
                          <Select value={strategyParams.targetDirection} onValueChange={(v) => handleStrategyParamChange('targetDirection', v)}>
                            <SelectTrigger className="w-24 h-8 text-sm" data-testid="select-target-direction">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="increase">aumento</SelectItem>
                              <SelectItem value="decrease">redução</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <Select value={strategyParams.targetType} onValueChange={(v) => handleStrategyParamChange('targetType', v)}>
                          <SelectTrigger className="h-8 text-sm" data-testid="select-target-type">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="profit">Lucro Bruto</SelectItem>
                            <SelectItem value="revenue">Faturamento</SelectItem>
                            <SelectItem value="volume">Volume</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </div>
                </RadioGroup>

                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-2 mt-3 flex items-center gap-2">
                    <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                    <p className="text-xs text-red-700">{error}</p>
                  </div>
                )}

                <Button
                  onClick={handleCalculate}
                  disabled={loading}
                  className="w-full mt-3 h-10 bg-cta hover:bg-cta/90 text-white font-bold"
                  data-testid="btn-calculate"
                >
                  {loading ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Calculando...</>
                  ) : (
                    <><Calculator className="w-4 h-4 mr-2" />Calcular Resultados</>
                  )}
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Results */}
          <div className="lg:col-span-4">
            <Card className="h-full">
              <CardHeader className="pb-2 pt-3 px-4">
                <CardTitle className="text-base font-heading font-bold text-infoprice-navy flex items-center gap-2">
                  <BarChart3 className="w-4 h-4 text-infoprice-blue" />
                  Resultados da Simulação
                </CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-4">
                {!results ? (
                  <EmptyResults />
                ) : (
                  <div className="space-y-3" data-testid="results-container">
                    {/* Warning */}
                    {results.message && results.message !== "Cálculo realizado com sucesso!" && (
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-2 flex items-start gap-2" data-testid="warning-message">
                        <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                        <p className="text-xs text-amber-800">{results.message}</p>
                      </div>
                    )}
                    
                    {/* Current Summary */}
                    <div className="bg-slate-50 rounded-lg p-3 border border-slate-100">
                      <h4 className="font-semibold text-xs text-slate-500 mb-2">CENÁRIO ATUAL</h4>
                      <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                        <div className="flex justify-between">
                          <span className="text-slate-500">Faturamento:</span>
                          <span className="font-semibold">{formatCurrency(results.current.revenue)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-500">Lucro:</span>
                          <span className="font-semibold">{formatCurrency(results.current.profit)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-500">Volume:</span>
                          <span className="font-semibold">{formatNumber(results.current.volume)} un.</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-500">Margem:</span>
                          <span className="font-semibold">{results.current.margin.toFixed(1)}%</span>
                        </div>
                      </div>
                    </div>

                    {/* New Results */}
                    <div>
                      <h4 className="font-semibold text-xs text-infoprice-navy mb-2 flex items-center gap-1">
                        <TrendingUp className="w-3 h-3 text-infoprice-blue" />
                        NOVA PROJEÇÃO
                      </h4>
                      <div className="grid grid-cols-2 gap-2">
                        <ResultMetric
                          label="Novo Preço"
                          value={formatCurrency(results.simulation.new_price)}
                          change={results.simulation.price_change_pct}
                          icon={DollarSign}
                        />
                        <ResultMetric
                          label="Faturamento"
                          value={formatCurrency(results.simulation.new_revenue)}
                          change={results.simulation.revenue_change_pct}
                          icon={TrendingUp}
                        />
                        <ResultMetric
                          label="Lucro Bruto"
                          value={formatCurrency(results.simulation.new_profit)}
                          change={results.simulation.profit_change_pct}
                          icon={BarChart3}
                        />
                        <ResultMetric
                          label="Volume"
                          value={`${formatNumber(Math.round(results.simulation.new_volume))} un.`}
                          change={results.simulation.volume_change_pct}
                          icon={Package}
                        />
                        <div className="col-span-2">
                          <ResultMetric
                            label="Margem"
                            value={`${results.simulation.new_margin.toFixed(1)}%`}
                            change={results.simulation.margin_change_pct}
                            icon={Target}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </TooltipProvider>
  );
};

export default PricingCalculator;
